import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { profileService } from '../../../services/profileService';

const ProfileEditForm = ({ profile, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    fullName: profile?.fullName || '',
    phone: profile?.phone || '',
    city: profile?.city || '',
    state: profile?.state || '',
    bio: profile?.bio || ''
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    if (passwordErrors?.[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }));
    }
    setPasswordSuccess('');
  };

  const validatePassword = () => {
    const errors = {};
    if (!passwordData?.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData?.newPassword?.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    if (!passwordData?.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    if (!validatePassword()) return;
    setPasswordSaving(true);
    try {
      const { error } = await profileService?.changePassword(passwordData?.newPassword);
      if (error) {
        setPasswordErrors({ newPassword: error?.message || 'Failed to update password' });
      } else {
        setPasswordSuccess('Password updated successfully!');
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setShowPasswordSection(false);
      }
    } catch (err) {
      setPasswordErrors({ newPassword: 'An unexpected error occurred' });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Info Form */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-6">
          Edit Profile
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email (read-only) */}
          <div>
            <label className="block font-body text-sm font-medium text-foreground mb-2">
              Email Address
            </label>
            <div className="flex items-center px-4 py-3 bg-muted border border-border rounded-lg">
              <Icon name="Mail" size={16} className="text-muted-foreground mr-2 flex-shrink-0" />
              <span className="font-body text-base text-muted-foreground">{profile?.email}</span>
              <span className="ml-auto text-xs font-caption text-muted-foreground bg-background px-2 py-1 rounded">Read-only</span>
            </div>
            <p className="mt-1 font-body text-xs text-muted-foreground">Email cannot be changed. Contact support if needed.</p>
          </div>

          {/* Full Name */}
          <div>
            <label className="block font-body text-sm font-medium text-foreground mb-2">
              Full Name
            </label>
            <Input
              type="text"
              name="fullName"
              value={formData?.fullName}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block font-body text-sm font-medium text-foreground mb-2">
              Phone Number
            </label>
            <Input
              type="tel"
              name="phone"
              value={formData?.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
            />
          </div>

          {/* Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-body text-sm font-medium text-foreground mb-2">
                City
              </label>
              <Input
                type="text"
                name="city"
                value={formData?.city}
                onChange={handleChange}
                placeholder="Enter your city"
              />
            </div>
            <div>
              <label className="block font-body text-sm font-medium text-foreground mb-2">
                State
              </label>
              <Input
                type="text"
                name="state"
                value={formData?.state}
                onChange={handleChange}
                placeholder="Enter your state"
              />
            </div>
          </div>

          {/* Bio / Summary */}
          <div>
            <label className="block font-body text-sm font-medium text-foreground mb-2">
              About {profile?.role === 'owner' ? 'Your Business' : 'Yourself'}
            </label>
            <textarea
              name="bio"
              value={formData?.bio}
              onChange={handleChange}
              placeholder={profile?.role === 'owner' ?'Tell renters about your business, trailers, and rental policies...' :'Tell owners a bit about yourself and how you plan to use trailers...'}
              rows={4}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-base text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              size="lg"
              fullWidth
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={saving}
              iconName={saving ? 'Loader' : 'Save'}
              iconPosition="left"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>

      {/* Password Change Section */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl font-semibold text-foreground">Password</h2>
            <p className="font-body text-sm text-muted-foreground mt-1">Update your account password</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName={showPasswordSection ? 'ChevronUp' : 'Lock'}
            iconPosition="left"
            onClick={() => {
              setShowPasswordSection(!showPasswordSection);
              setPasswordData({ newPassword: '', confirmPassword: '' });
              setPasswordErrors({});
              setPasswordSuccess('');
            }}
          >
            {showPasswordSection ? 'Cancel' : 'Change Password'}
          </Button>
        </div>

        {passwordSuccess && (
          <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <span className="font-body text-sm text-success">{passwordSuccess}</span>
          </div>
        )}

        {showPasswordSection && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4 pt-2 border-t border-border">
            <div className="mt-4">
              <Input
                type="password"
                name="newPassword"
                label="New Password"
                placeholder="At least 8 characters"
                value={passwordData?.newPassword}
                onChange={handlePasswordChange}
                error={passwordErrors?.newPassword}
                required
              />
            </div>
            <div>
              <Input
                type="password"
                name="confirmPassword"
                label="Confirm New Password"
                placeholder="Re-enter your new password"
                value={passwordData?.confirmPassword}
                onChange={handlePasswordChange}
                error={passwordErrors?.confirmPassword}
                required
              />
            </div>
            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={passwordSaving}
                iconName={passwordSaving ? 'Loader' : 'Lock'}
                iconPosition="left"
              >
                {passwordSaving ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfileEditForm;